// hooks/use-column-manager.ts
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import type { ColumnsType, ColumnGroupType } from 'antd/es/table'

interface UseColumnManagerProps {
    allColumns: ColumnsType<any>
    storageKey?: string
    defaultVisible?: string[]
}

export interface ColumnSetting {
    key: string
    title: string
    visible: boolean
    children?: ColumnSetting[]
    isGroup?: boolean
}

interface UseColumnManagerReturn {
    visibleColumns: ColumnsType<any>
    visibleColumnKeys: string[]
    hiddenColumnKeys: string[]
    toggleColumn: (columnKey: string) => void
    showColumn: (columnKey: string) => void
    hideColumn: (columnKey: string) => void
    showAllColumns: () => void
    hideAllColumns: () => void
    resetToDefault: () => void
    isColumnVisible: (columnKey: string) => boolean
    columnSettings: ColumnSetting[]
}

// Тип для колонки с children
interface ColumnWithChildren {
    children?: ColumnsType<any>
    key?: React.Key
    title?: any
    [key: string]: any
}

// Управляемая колонка: есть ключ и НЕпустой заголовок, и колонку не исключили флагом hideInColumnManager
const isManageableColumn = (col: ColumnWithChildren): boolean =>
    Boolean(col.key && col.title && col.title !== '' && !col.hideInColumnManager)

// Функция для рекурсивного извлечения колонок с ключами и НЕпустыми заголовками
const getManageableColumns = (columns: ColumnsType<any>): ColumnsType<any> => {
    const result: ColumnsType<any> = []

    columns.forEach((col: ColumnWithChildren) => {
        if (col.children) {
            // Обрабатываем вложенные колонки
            const manageableChildren = getManageableColumns(col.children)
            result.push(...manageableChildren)
        } else if (isManageableColumn(col)) {
            // Добавляем только колонки с ключом и НЕпустым заголовком
            result.push(col)
        }
        // Колонки без ключа или с пустым заголовком игнорируем
    })

    return result
}

// Функция для получения ВСЕХ колонок (включая неуправляемые) с сохранением структуры
const getAllColumnsWithVisibility = (
    columns: ColumnsType<any>,
    visibleColumnKeys: string[]
): ColumnsType<any> => {
    return columns
        .map((col: ColumnWithChildren) => {
            const isColumnGroup = 'children' in col && col.children !== undefined
            if (isColumnGroup) {
                // Рекурсивно обрабатываем детей
                const groupCol = col as ColumnGroupType<any>
                const visibleChildren = getAllColumnsWithVisibility(
                    groupCol.children,
                    visibleColumnKeys
                )

                // Если все дочерние элементы скрыты, скрываем и группу
                if (visibleChildren.length === 0) {
                    return null
                }

                return {
                    ...col,
                    children: visibleChildren,
                }
            } else {
                // Для обычных колонок
                if (isManageableColumn(col)) {
                    // Управляемая колонка - показываем только если видима
                    return visibleColumnKeys.includes(col.key as string) ? col : null
                } else {
                    // Неуправляемая колонка (без ключа или с пустым заголовком) - всегда показываем
                    return col
                }
            }
        })
        .filter(Boolean) as ColumnsType<any>
}

// извлечение строки из заголовка колонки
function textFromTitle(node: React.ReactNode): string {

    if (typeof node === 'string' || typeof node === 'number') {
        return String(node)
    }

    if (Array.isArray(node)) {
        return node.map(textFromTitle).filter(Boolean).join(' ')
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
        return textFromTitle(node.props.children)
    }

    return ""
}

export const useColumnManager = ({
    allColumns,
    storageKey,
    defaultVisible,
}: UseColumnManagerProps): UseColumnManagerReturn => {
    // Получаем только управляемые колонки (с ключами и НЕпустыми заголовками)
    const manageableColumns = useMemo(() => {
        return getManageableColumns(allColumns)
    }, [allColumns])

    // Получаем все ключи управляемых колонок
    const allColumnKeys = useMemo(
        () => manageableColumns.map(col => col.key as string).filter(Boolean),
        [manageableColumns]
    )

    // Начальное состояние - ВСЕ управляемые колонки видимы
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(allColumnKeys)
    const [isInitialized, setIsInitialized] = useState(false)

    // Инициализация из localStorage
    useEffect(() => {
        if (!storageKey || isInitialized) return

        try {
            const saved = localStorage.getItem(storageKey)
            if (saved) {
                const savedKeys = JSON.parse(saved) as string[]
                const validKeys = savedKeys.filter(
                    key => typeof key === 'string' && allColumnKeys.includes(key)
                )

                if (validKeys.length > 0) {
                    setVisibleColumnKeys(validKeys)
                }
            }
            setIsInitialized(true)
        } catch (error) {
            console.warn('Failed to load column settings:', error)
            setIsInitialized(true)
        }
    }, [storageKey, allColumnKeys, isInitialized])

    // Сохранение в localStorage
    useEffect(() => {
        if (storageKey && isInitialized) {
            try {
                localStorage.setItem(storageKey, JSON.stringify(visibleColumnKeys))
            } catch (error) {
                console.warn('Failed to save column settings:', error)
            }
        }
    }, [visibleColumnKeys, storageKey, isInitialized])

    // Получаем все дочерние ключи для группы
    const getChildrenKeys = useCallback(
        (groupKey: string): string[] => {
            const result: string[] = []
            const findGroup = (cols: ColumnsType<any>): ColumnWithChildren | null => {
                for (const col of cols) {
                    const isColumnGroup = 'children' in col && col.children !== undefined
                    if (col.key === groupKey && isColumnGroup) {
                        return col
                    }
                    if (isColumnGroup) {
                        const groupCol = col as ColumnGroupType<any>
                        const found = findGroup(groupCol.children)
                        if (found) return found
                    }
                }
                return null
            }

            const group = findGroup(allColumns)
            if (group) {
                const isGroupColumn = 'children' in group && group.children !== undefined
                if (isGroupColumn) {
                    const groupCol = group as ColumnGroupType<any>
                    const extractKeys = (cols: ColumnsType<any>) => {
                        cols.forEach(col => {
                            if (isManageableColumn(col)) {
                                result.push(col.key as string)
                            }
                            const isColGroup = 'children' in col && col.children !== undefined
                            if (isColGroup) {
                                const colGroup = col as ColumnGroupType<any>
                                extractKeys(colGroup.children)
                            }
                        })
                    }
                    extractKeys(groupCol.children)
                }
            }
            return result
        },
        [allColumns]
    )

    // Переключение видимости колонки
    const toggleColumn = useCallback(
        (columnKey: string) => {
            setVisibleColumnKeys(prev => {
                const childrenKeys = getChildrenKeys(columnKey)
                const isGroup = childrenKeys.length > 0

                if (isGroup) {
                    let newKeys = [...prev]
                    const allChildrenVisible =
                        childrenKeys.length > 0 && childrenKeys.every(key => newKeys.includes(key))

                    if (allChildrenVisible) {
                        // Скрываем все дочерние колонки группы
                        newKeys = newKeys.filter(key => !childrenKeys.includes(key))
                    } else {
                        // Показываем все дочерние колонки группы
                        childrenKeys.forEach(childKey => {
                            if (!newKeys.includes(childKey)) {
                                newKeys.push(childKey)
                            }
                        })
                    }

                    if (newKeys.length === 0) {
                        return prev
                    }

                    return newKeys
                }

                // Защита от скрытия последней колонки
                if (prev.includes(columnKey) && prev.length <= 1) {
                    return prev
                }

                const isCurrentlyVisible = prev.includes(columnKey)
                let newKeys: string[]

                if (isCurrentlyVisible) {
                    // Скрываем колонку
                    newKeys = prev.filter(key => key !== columnKey)
                } else {
                    // Показываем колонку
                    newKeys = [...prev, columnKey]
                }

                return newKeys.length > 0 ? newKeys : [allColumnKeys[0]]
            })
        },
        [allColumnKeys, getChildrenKeys]
    )

    // Показать колонку
    const showColumn = useCallback((columnKey: string) => {
        setVisibleColumnKeys(prev => (prev.includes(columnKey) ? prev : [...prev, columnKey]))
    }, [])

    // Скрыть колонку
    const hideColumn = useCallback(
        (columnKey: string) => {
            setVisibleColumnKeys(prev => {
                const newKeys = prev.filter(key => key !== columnKey)
                return newKeys.length > 0 ? newKeys : [allColumnKeys[0]]
            })
        },
        [allColumnKeys]
    )

    // Показать все колонки
    const showAllColumns = useCallback(() => {
        setVisibleColumnKeys(allColumnKeys)
    }, [allColumnKeys])

    // Скрыть все колонки
    const hideAllColumns = useCallback(() => {
        setVisibleColumnKeys([allColumnKeys[0]])
    }, [allColumnKeys])

    // Сбросить к настройкам по умолчанию
    const resetToDefault = useCallback(() => {
        if (defaultVisible && defaultVisible.length > 0) {
            const validDefaultKeys = defaultVisible.filter(key => allColumnKeys.includes(key))
            setVisibleColumnKeys(validDefaultKeys.length > 0 ? validDefaultKeys : allColumnKeys)
        } else {
            setVisibleColumnKeys(allColumnKeys)
        }
    }, [defaultVisible, allColumnKeys])

    // Проверка видимости колонки
    const isColumnVisible = useCallback(
        (columnKey: string) => {
            return visibleColumnKeys.includes(columnKey)
        },
        [visibleColumnKeys]
    )

    // Получаем видимые колонки с сохранением структуры
    const visibleColumns = useMemo(() => {
        if (!isInitialized) return allColumns // Пока не инициализированы, показываем все колонки
        return getAllColumnsWithVisibility(allColumns, visibleColumnKeys)
    }, [allColumns, visibleColumnKeys, isInitialized])

    // Скрытые колонки
    const hiddenColumnKeys = useMemo(
        () => allColumnKeys.filter(key => !visibleColumnKeys.includes(key)),
        [allColumnKeys, visibleColumnKeys]
    )

    // Рекурсивная функция для построения иерархической структуры настроек
    const buildColumnSettings = useCallback(
        (columns: ColumnsType<any>): ColumnSetting[] => {
            const result: ColumnSetting[] = []

            columns.forEach((col: ColumnWithChildren) => {
                // Пропускаем колонки без ключа или с пустым заголовком (не управляемые)
                if (!isManageableColumn(col)) {
                    return
                }

                const colKey = col.key as string
                const colTitle = textFromTitle(col.title)

                // Проверяем, является ли колонка группой
                const isColumnGroup =
                    'children' in col && col.children !== undefined && col.children.length > 0

                if (isColumnGroup) {
                    // Это группа - обрабатываем дочерние элементы
                    const groupCol = col as ColumnGroupType<any>
                    const childrenSettings = buildColumnSettings(groupCol.children)

                    // Проверяем, есть ли хотя бы один видимый дочерний элемент
                    // Группа видима, если хотя бы один дочерний элемент видим
                    const hasVisibleChildren =
                        childrenSettings.length > 0 && childrenSettings.some(child => child.visible)

                    result.push({
                        key: colKey,
                        title: colTitle,
                        visible: hasVisibleChildren,
                        isGroup: true,
                        children: childrenSettings,
                    })
                } else {
                    // Обычная колонка
                    result.push({
                        key: colKey,
                        title: colTitle,
                        visible: isColumnVisible(colKey),
                    })
                }
            })

            return result
        },
        [isColumnVisible]
    )

    // Настройки для отображения в UI с иерархией
    const columnSettings = useMemo(() => {
        return buildColumnSettings(allColumns)
    }, [allColumns, buildColumnSettings])

    return {
        visibleColumns,
        visibleColumnKeys,
        hiddenColumnKeys,
        toggleColumn,
        showColumn,
        hideColumn,
        showAllColumns,
        hideAllColumns,
        resetToDefault,
        isColumnVisible,
        columnSettings,
    }
}
